import { useEffect, useMemo, useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import { createPortal } from "react-dom";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import {
  ALL_COLUMNS,
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  defaultVisibleColumnsForTab,
  pinnedKeysForTab,
  saveVisibleColumns,
  type ColumnCategory,
  type ColumnDef,
  type ColumnKey,
} from "@/lib/columns-config";
import { useCreateColumnPreset } from "@/lib/api-hooks";

type ColumnsModalProps = {
  open: boolean;
  onClose: () => void;
  visible: ColumnKey[];
  onChange: (next: ColumnKey[]) => void;
  tab?: string;
  allowedKeys?: ColumnKey[];
  /** Live per-column width map from StatsTable resize state. When
   *  user clicks "Save as preset", these widths are persisted along
   *  with the visible columns + order. */
  currentWidths?: Record<string, number>;
};

/**
 * Mid-pane checkbox row. Pulled out of the JSX so the same renderer
 * can be reused both by the per-category list and the cross-category
 * search results.
 */
function renderCheckboxRow(
  c: ColumnDef,
  pinnedSet: Set<ColumnKey>,
  draftSet: Set<ColumnKey>,
  toggle: (k: ColumnKey) => void
) {
  const isPinned = pinnedSet.has(c.key);
  return (
    <label
      key={c.key}
      className="check"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "7px 6px",
        fontSize: 13.5,
        borderRadius: 6,
        cursor: isPinned ? "not-allowed" : "pointer",
        opacity: isPinned ? 0.65 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isPinned)
          ((e.currentTarget as HTMLLabelElement).style.background =
            "var(--c-surface-sunk)");
      }}
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLLabelElement).style.background = "transparent")
      }
      title={isPinned ? "Pinned — always visible" : undefined}
    >
      <input
        type="checkbox"
        checked={draftSet.has(c.key)}
        onChange={() => toggle(c.key)}
        disabled={isPinned}
      />
      <span style={{ flex: 1 }}>{c.label}</span>
      {isPinned && (
        <span
          style={{
            fontSize: 10,
            color: "var(--c-text-4)",
            fontWeight: 500,
            letterSpacing: ".05em",
            textTransform: "uppercase",
          }}
        >
          Pinned
        </span>
      )}
    </label>
  );
}

/**
 * Three-pane columns picker. Layout:
 *
 *   ┌──────────────┬──────────────────────┬──────────────────────┐
 *   │ category rail│ checkbox list (mid)  │ Active columns (right│
 *   │ + presets    │  filtered by search  │  draggable + pinned  │
 *   └──────────────┴──────────────────────┴──────────────────────┘
 *
 * Right panel:
 *   - Identity row (campaign name / ad group name / keyword) at the
 *     very top — locked, has no ColumnKey because StatsTable always
 *     renders it as the first cell, but the picker visualises it so
 *     the pin count matches the spec (campaigns 3, ad_groups 2,
 *     keywords 2).
 *   - Pinned columns from `pinnedKeysForTab` (lock icon, no checkbox,
 *     no drag).
 *   - User-selected non-pinned columns: drag-reorder via HTML5 native
 *     drag-and-drop, × removes (toggles off the checkbox in the mid
 *     pane).
 *
 * Footer:
 *   - "Save as preset" checkbox → reveals name input + inline Save
 *     button. Save button persists the preset on click without
 *     waiting for the modal's Apply (so the preset survives even if
 *     the user later cancels the column edits).
 *   - Reset / Cancel / Apply on the right.
 *
 * Search filters across ALL categories at once: when the query box is
 * non-empty, the rail's selected category is ignored and matches from
 * every category surface in the mid pane.
 *
 * Internally state is `draftOrder: ColumnKey[]` (an ordered array
 * containing every selected key — pinned first, then user-ordered
 * body). `draftSet` is a derived Set for fast .has() checks. On
 * Apply, the final order is persisted via `saveVisibleColumns`.
 */
export function ColumnsModal({
  open,
  onClose: onCloseProp,
  visible,
  onChange,
  tab = "campaigns",
  allowedKeys,
  currentWidths,
}: ColumnsModalProps) {
  const columns = useMemo(
    () =>
      allowedKeys
        ? ALL_COLUMNS.filter((c) => allowedKeys.includes(c.key))
        : ALL_COLUMNS,
    [allowedKeys]
  );

  const pinned = useMemo(() => pinnedKeysForTab(tab), [tab]);
  const pinnedSet = useMemo(() => new Set(pinned), [pinned]);

  const [draftOrder, setDraftOrder] = useState<ColumnKey[]>(() => visible);
  const [active, setActive] = useState<ColumnCategory>("performance");
  const [query, setQuery] = useState("");

  const [savePresetOn, setSavePresetOn] = useState(false);
  const [presetName, setPresetName] = useState("");
  const createPreset = useCreateColumnPreset();
  const [presetError, setPresetError] = useState<string | null>(null);
  const [presetSavedAt, setPresetSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      // Always force pinned to be at the front when reopening — guards
      // against schema drift (e.g. an old localStorage payload that
      // pre-dates the pinning feature).
      const stripped = visible.filter((k) => !pinnedSet.has(k));
      setDraftOrder([...pinned, ...stripped]);
      setQuery("");
      setSavePresetOn(false);
      setPresetName("");
      setPresetError(null);
      setPresetSavedAt(null);
    }
  }, [open, visible, pinned, pinnedSet]);

  const guard = useDiscardGuard(open, JSON.stringify(draftOrder) !== JSON.stringify([...pinned, ...visible.filter((k) => !pinnedSet.has(k))]) || presetName.trim() !== "", onCloseProp);
  const onClose = guard.requestClose;

  const draftSet = useMemo(() => new Set(draftOrder), [draftOrder]);

  const grouped = useMemo(() => {
    const m: Record<ColumnCategory, ColumnDef[]> = {
      performance: [],
      enriched: [],
      cohort: [],
      parameters: [],
      conversions: [],
    };
    for (const c of columns) m[c.category].push(c);
    return m;
  }, [columns]);

  const visibleCats = CATEGORY_ORDER.filter((cat) => grouped[cat].length > 0);

  // When the search box is empty, the mid pane shows columns from the
  // rail's active category. When the user types, we ignore the rail
  // selection and search every category at once — grouped output keeps
  // the visual hierarchy so users still see WHERE each match lives.
  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  const filteredForActive = useMemo(() => {
    if (isSearching) return [];
    return grouped[active];
  }, [grouped, active, isSearching]);

  const searchHits = useMemo(() => {
    if (!isSearching) return [] as { cat: ColumnCategory; cols: ColumnDef[] }[];
    return CATEGORY_ORDER.map((cat) => ({
      cat,
      cols: grouped[cat].filter((c) =>
        c.label.toLowerCase().includes(trimmedQuery)
      ),
    })).filter((g) => g.cols.length > 0);
  }, [grouped, isSearching, trimmedQuery]);

  // Right-panel active list: identity row first (no ColumnKey, locked),
  // then pinned columns (locked), then user-ordered non-pinned (drag).
  const identityLabel: string | null = (() => {
    switch (tab) {
      case "campaigns":
        return "Campaign name";
      case "ad_groups":
        return "Ad group name";
      case "keywords":
        return "Keyword";
      default:
        return null;
    }
  })();

  const pinnedDefs = useMemo(
    () =>
      pinned
        .map((k) => columns.find((c) => c.key === k))
        .filter((c): c is ColumnDef => c !== undefined),
    [pinned, columns]
  );

  const totalPinned = pinnedDefs.length + (identityLabel ? 1 : 0);

  const bodyOrdered = useMemo(
    () =>
      draftOrder
        .filter((k) => !pinnedSet.has(k))
        .map((k) => columns.find((c) => c.key === k))
        .filter((c): c is ColumnDef => c !== undefined),
    [draftOrder, pinnedSet, columns]
  );

  // --- Drag-and-drop reorder (non-pinned section only) ---
  // HTML5 native draggable on each row. dragKey holds the key being
  // dragged; dragOverKey is the row currently hovered. On drop we
  // splice in the dragged key at the hover row's position. Hooks
  // declared above the early `return null` so React's hooks-of-rules
  // is happy across open/closed transitions.
  const [dragKey, setDragKey] = useState<ColumnKey | null>(null);
  const [dragOverKey, setDragOverKey] = useState<ColumnKey | null>(null);

  if (!open) return null;

  const toggle = (key: ColumnKey) => {
    if (pinnedSet.has(key)) return; // pinned can't be toggled off
    setDraftOrder((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      return [...prev, key];
    });
  };

  const removeFromActive = (key: ColumnKey) => {
    if (pinnedSet.has(key)) return;
    setDraftOrder((prev) => prev.filter((k) => k !== key));
  };

  const handleDrop = (target: ColumnKey) => {
    if (dragKey == null || dragKey === target) {
      setDragKey(null);
      setDragOverKey(null);
      return;
    }
    setDraftOrder((prev) => {
      // Splice dragKey out, then re-insert at target's index. Pinned
      // entries are unaffected because filtering happens in
      // bodyOrdered, not here.
      const out = prev.filter((k) => k !== dragKey);
      const targetIdx = out.indexOf(target);
      if (targetIdx === -1) return prev;
      out.splice(targetIdx, 0, dragKey);
      return out;
    });
    setDragKey(null);
    setDragOverKey(null);
  };

  const commit = () => {
    saveVisibleColumns(tab, draftOrder);
    onChange(draftOrder);
    onCloseProp();
  };

  const savePresetNow = async () => {
    const name = presetName.trim();
    if (!name) return;
    setPresetError(null);
    try {
      await createPreset.mutateAsync({
        tab: tab as "campaigns" | "ad_groups" | "keywords",
        name,
        columns: draftOrder,
        // Snapshot the live resize state into the preset so reloading
        // it later restores both visibility/order AND per-column
        // widths (manual-resize feature, migration 0063).
        widths: currentWidths,
      });
      setPresetSavedAt(Date.now());
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "Couldn't save preset";
      setPresetError(msg);
    }
  };

  const reset = () => {
    setDraftOrder(defaultVisibleColumnsForTab(tab));
  };

  // Render via portal so the modal escapes the sticky toolbar's
  // stacking context. Without this, `position: fixed` + `zIndex: 100`
  // is locally interpreted INSIDE the parent's stacking context (the
  // .ads-sticky-toolbar at z:5), so the topbar (z:10) and breadcrumbs
  // (z:6) STILL paint over the modal in their visual area.
  // Portaling to document.body puts the modal at the root and its
  // zIndex becomes globally authoritative.
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14,18,32,0.45)",
        zIndex: 100,
        display: "grid",
        placeItems: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="card"
        style={{
          width: "min(1000px, 96vw)",
          height: "min(720px, 92vh)",
          display: "grid",
          gridTemplateRows: "auto auto 1fr auto",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 22px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em" }}>
            Edit columns
          </div>
          <Btn variant="icon" onClick={onClose} title="Close">
            {I.x}
          </Btn>
        </div>

        {/* Search */}
        <div style={{ padding: "0 22px 14px" }}>
          <div className="input-group" style={{ width: "60%", minWidth: 260 }}>
            {I.search}
            <input
              className="input"
              placeholder="Search metrics"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Body: rail | checkboxes | reorder */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "200px 1fr 320px",
            borderTop: "1px solid var(--c-divider)",
            minHeight: 0,
          }}
        >
          {/* Category rail */}
          <div
            style={{
              borderRight: "1px solid var(--c-divider)",
              padding: 10,
              overflowY: "auto",
            }}
          >
            {visibleCats.map((cat) => {
              const isActive = cat === active;
              const selectedInCat = grouped[cat].filter((c) =>
                draftSet.has(c.key)
              ).length;
              return (
                <div
                  key={cat}
                  onClick={() => setActive(cat)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontSize: 13.5,
                    background: isActive
                      ? "var(--c-surface-sunk)"
                      : "transparent",
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "var(--c-text)" : "var(--c-text-2)",
                    marginBottom: 2,
                  }}
                >
                  <span>{CATEGORY_LABEL[cat]}</span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--c-text-4)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {selectedInCat}/{grouped[cat].length}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Checkboxes */}
          <div style={{ padding: "14px 18px", overflowY: "auto" }}>
            {!isSearching && (
              <>
                <div
                  className="muted"
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                    marginBottom: 10,
                  }}
                >
                  {CATEGORY_LABEL[active]}
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {filteredForActive.map((c) =>
                    renderCheckboxRow(c, pinnedSet, draftSet, toggle)
                  )}
                  {filteredForActive.length === 0 && (
                    <div
                      className="muted"
                      style={{ padding: 14, fontSize: 13, textAlign: "center" }}
                    >
                      No columns in this category.
                    </div>
                  )}
                </div>
              </>
            )}
            {isSearching && (
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {searchHits.map((g) => (
                  <div key={g.cat} style={{ marginBottom: 10 }}>
                    <div
                      className="muted"
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: ".08em",
                        marginBottom: 6,
                      }}
                    >
                      {CATEGORY_LABEL[g.cat]}
                    </div>
                    {g.cols.map((c) =>
                      renderCheckboxRow(c, pinnedSet, draftSet, toggle)
                    )}
                  </div>
                ))}
                {searchHits.length === 0 && (
                  <div
                    className="muted"
                    style={{ padding: 14, fontSize: 13, textAlign: "center" }}
                  >
                    No columns match your search.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reorder / selected preview (right) */}
          <div
            style={{
              borderLeft: "1px solid var(--c-divider)",
              padding: "14px 16px",
              overflowY: "auto",
              background: "var(--c-surface-alt)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                Active columns
              </span>
              <span
                style={{
                  background: "var(--c-accent-600)",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                }}
              >
                {totalPinned + bodyOrdered.length}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              {/* Identity: rendered separately by StatsTable but shown
                  here so the visible pin count matches spec
                  (campaigns 3 / ad_groups 2 / keywords 2). */}
              {identityLabel && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 6px",
                    fontSize: 12.5,
                    color: "var(--c-text-2)",
                    background: "rgba(0,0,0,.02)",
                    borderRadius: 4,
                    marginBottom: 2,
                  }}
                  title="Pinned — always visible"
                >
                  <span style={{ width: 16, color: "var(--c-text-4)" }}>🔒</span>
                  <span style={{ flex: 1, fontWeight: 500 }}>
                    {identityLabel}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--c-text-4)",
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                    }}
                  >
                    Pinned
                  </span>
                </div>
              )}
              {/* Pinned: locked, no drag, no remove. Always at top. */}
              {pinnedDefs.map((c) => (
                <div
                  key={c.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 6px",
                    fontSize: 12.5,
                    color: "var(--c-text-2)",
                    background: "rgba(0,0,0,.02)",
                    borderRadius: 4,
                    marginBottom: 2,
                  }}
                  title="Pinned — always visible"
                >
                  <span style={{ width: 16, color: "var(--c-text-4)" }}>🔒</span>
                  <span style={{ flex: 1, fontWeight: 500 }}>{c.label}</span>
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--c-text-4)",
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                    }}
                  >
                    Pinned
                  </span>
                </div>
              ))}

              {/* Body: draggable rows */}
              {bodyOrdered.map((c) => {
                const isDragOver = dragOverKey === c.key && dragKey !== c.key;
                return (
                  <div
                    key={c.key}
                    draggable
                    onDragStart={(e) => {
                      setDragKey(c.key);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverKey(c.key);
                    }}
                    onDragLeave={() => {
                      if (dragOverKey === c.key) setDragOverKey(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDrop(c.key);
                    }}
                    onDragEnd={() => {
                      setDragKey(null);
                      setDragOverKey(null);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "7px 6px",
                      fontSize: 12.5,
                      color: "var(--c-text-2)",
                      cursor: "grab",
                      borderTop: isDragOver
                        ? "2px solid var(--c-accent-600)"
                        : "2px solid transparent",
                      opacity: dragKey === c.key ? 0.4 : 1,
                    }}
                  >
                    <span
                      style={{
                        color: "var(--c-text-4)",
                        cursor: "grab",
                        display: "inline-flex",
                      }}
                    >
                      {I.drag}
                    </span>
                    <span style={{ color: "var(--c-text-4)", fontSize: 10, width: 36 }}>
                      {CATEGORY_LABEL[c.category].slice(0, 4)}
                    </span>
                    <span style={{ flex: 1 }}>{c.label}</span>
                    <button
                      onClick={() => removeFromActive(c.key)}
                      style={{
                        color: "var(--c-text-4)",
                        fontSize: 16,
                        background: "transparent",
                        border: 0,
                        cursor: "pointer",
                        padding: 0,
                      }}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                );
              })}

              {totalPinned + bodyOrdered.length === 0 && (
                <div className="muted" style={{ fontSize: 12, padding: 8 }}>
                  No columns selected yet.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 20px",
            borderTop: "1px solid var(--c-divider)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--c-surface)",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Btn size="sm" onClick={reset}>
              Reset to defaults
            </Btn>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                color: "var(--c-text-2)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={savePresetOn}
                onChange={(e) => {
                  setSavePresetOn(e.target.checked);
                  if (!e.target.checked) {
                    setPresetName("");
                    setPresetError(null);
                    setPresetSavedAt(null);
                  }
                }}
              />
              Save as preset
            </label>
            {savePresetOn && (
              <>
                <input
                  className="input sm"
                  placeholder="Preset name"
                  value={presetName}
                  onChange={(e) => {
                    setPresetName(e.target.value);
                    setPresetError(null);
                    setPresetSavedAt(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && presetName.trim()) {
                      e.preventDefault();
                      void savePresetNow();
                    }
                  }}
                  style={{ width: 180 }}
                />
                <Btn
                  size="sm"
                  variant="primary"
                  onClick={() => void savePresetNow()}
                  disabled={
                    createPreset.isPending || presetName.trim().length === 0
                  }
                >
                  {createPreset.isPending ? "Saving…" : "Save"}
                </Btn>
                {presetSavedAt && !presetError && (
                  <span
                    style={{
                      fontSize: 12,
                      color: "var(--c-pos-600)",
                    }}
                  >
                    Saved
                  </span>
                )}
              </>
            )}
            {presetError && (
              <span
                style={{
                  color: "var(--c-neg-600)",
                  fontSize: 12,
                }}
              >
                {presetError}
              </span>
            )}
          </div>
          <div className="row gap-8">
            <Btn onClick={onClose}>Cancel</Btn>
            <Btn variant="primary" onClick={commit}>
              Apply
            </Btn>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
